import type { Cell, Row } from "exceljs";
import type { Department } from "../../../contexts/AuthProvider";
import type { PrintoutSchedule } from "../../../utils/models";
import { getJobDisplayName } from "../../../utils/roles";
import { MISSING_SHIFT_PLACEHOLDER } from "../constants";
import type { PrintoutColumn, RowNoteFlags, ScheduleRow } from "../types";
import { resolveRoleShortCode } from "../util/roleHelpers";
import { getRowScheduledHours } from "../util/scheduledHours";
import { formatShiftTimeRange } from "../util/shiftTimeFormat";
import { PENDING_DELETE_FILL, PENDING_FILL, THIN_BORDER } from "./cellStyle";
import { composeRowNotes } from "./rowNotes";

interface PopulateCellArgs {
	cell: Cell;
	col: PrintoutColumn;
	row: ScheduleRow | undefined;
	hospitalData: PrintoutSchedule;
	timezone: string;
	department: Department;
	jobManagementEnabled: boolean;
	noteFlags: RowNoteFlags | undefined;
	appendRoleToName: boolean;
}

/**
 * Populate one cell based on its column kind and the row it represents.
 *
 * Mirrors the old `populateCellForRow` exactly:
 * - `location` → `row.location || ""`
 * - `name` → `row.nurse.name` (bold if charge) or `MISSING_SHIFT_PLACEHOLDER` (mapping §9.4)
 * - `certification` → role short code (always raw role; mapping §5.2 Certification)
 * - `shiftTime` → `"<start> - <end>"`
 * - `role` → role short code, OR job display name when JobManagement is on (mapping §5.2)
 * - `notes` → composed from `buildNurseNotes` (mapping §9.1)
 * - `initials` → always empty (today's behavior — initials column reserved for future use)
 * - `floor` → `row.floor || ""`
 * - `scheduledHours` → net of break rules, as a number so Excel can sum it; blank on gap rows
 *
 * Empty rows (undefined `row`) produce an empty cell value.
 */
export const populateCell = ({
	cell,
	col,
	row,
	hospitalData,
	timezone,
	department,
	jobManagementEnabled,
	noteFlags,
	appendRoleToName,
}: PopulateCellArgs): void => {
	if (!row) {
		cell.value = "";
		return;
	}
	switch (col.type) {
		case "location":
			cell.value = row.location || "";
			break;
		case "name":
			if (row.nurse) {
				const roleSuffix = appendRoleToName ? `, ${resolveRoleShortCode(row.nurse.role, row.role)}` : "";
				cell.value = `${row.nurse.name}${roleSuffix}`;
				if (row.nurse.charge) cell.font = { bold: true };
			} else {
				cell.value = MISSING_SHIFT_PLACEHOLDER;
			}
			break;
		case "certification":
			cell.value = resolveRoleShortCode(row.nurse?.role, row.role);
			break;
		case "shiftTime":
			cell.value = formatShiftTimeRange(row.shiftStart, row.shiftEnd, timezone);
			break;
		case "role":
			if (jobManagementEnabled) {
				cell.value = row.nurse?.jobId ? getJobDisplayName(row.nurse.jobId, department) : "";
			} else {
				cell.value = resolveRoleShortCode(row.nurse?.role, row.role);
			}
			break;
		case "notes":
			cell.value = composeRowNotes({ nurse: row.nurse, hospitalData, flags: noteFlags });
			break;
		case "initials":
			cell.value = "";
			break;
		case "floor":
			cell.value = row.floor || "";
			break;
		case "scheduledHours":
			// DEFAULT: gap rows have no staffed shift to net breaks from, so the cell is left blank.
			cell.value = getRowScheduledHours({ row, department, jobManagementEnabled }) ?? "";
			break;
	}
};

interface ApplyRowDecorationArgs {
	row: Row;
	scheduleRow: ScheduleRow | undefined;
	maxColumn: number;
}

/**
 * Apply PTO strikethrough / pending-change highlight / pending-delete highlight to all cells in a row.
 *
 * Precedence (one wins): pending-delete > pending > PTO. Cells without a `nurse` (gap placeholders)
 * receive no decoration beyond the standard border.
 */
export const applyRowDecoration = ({ row, scheduleRow, maxColumn }: ApplyRowDecorationArgs): void => {
	for (let col = 1; col <= maxColumn; col++) {
		row.getCell(col).border = THIN_BORDER;
	}
	const nurse = scheduleRow?.nurse;
	if (!nurse) return;

	if (nurse.isPendingToDelete) {
		for (let col = 1; col <= maxColumn; col++) row.getCell(col).fill = PENDING_DELETE_FILL;
	} else if (nurse.isPending) {
		for (let col = 1; col <= maxColumn; col++) row.getCell(col).fill = PENDING_FILL;
	} else if (nurse.isPtoShift) {
		for (let col = 1; col <= maxColumn; col++) row.getCell(col).font = { strike: true };
	}
};
